import { merge, max } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import {
  line,
  curveBasis,
  curveLinear,
  curveNatural,
  curveMonotoneX,
  curveMonotoneY,
  curveStep,
  curveStepBefore,
  curveStepAfter
} from 'd3-shape'

import ChartTemplate from '../../html/components/chart.html'
import ChartIconTemplate from '../../html/components/chart-icon.html'
import ChartLineTemplate from '../../html/components/chart-line.html'
import ChartAxisTemplate from '../../html/components/chart-axis.html'
import ChartGridTemplate from '../../html/components/chart-grid.html'

import Component from '../shared/component'
import Relation, {HasMany, HasOne, MemberOf} from '../shared/mixins/relation'

const chartPlots = Relation()
const chartAxis = Relation()
const chartGrid = Relation()

export const Chart = Component('v-chart', {
  mixins: [
    HasMany(chartPlots, 'plots'),
    HasMany(chartAxis, 'axis'),
    HasOne(chartGrid, 'grid')
  ],
  template: ChartTemplate,
  data () {
    return {
      bounds: {},
      regions: null
    }
  },
  computed: {
    classes () {
      const {axis} = this

      return {
        'has-axis-top': axis.some(a => a.side === 'top'),
        'has-axis-right': axis.some(a => a.side === 'right'),
        'has-axis-bottom': axis.some(a => a.side === 'bottom'),
        'has-axis-left': axis.some(a => a.side === 'left')
      }
    },

    data () {
      return this.plots.map(plot => plot.points)
    },

    counts () {
      const {bounds} = this

      const count = size => Math.max(Math.round(size / 100), 2)

      return {
        x: count(bounds.width),
        y: count(bounds.height)
      }
    },

    scale () {
      const {data, bounds, counts} = this

      if (data.length === 0) {
        return
      }

      const merged = merge(data)

      const x = scaleLinear()
        .domain([
          0,
          max(merged, d => d.x)
        ])
        .range([
          bounds.x,
          bounds.x + bounds.width
        ])
        .nice(counts.x)

      const y = scaleLinear()
        .domain([
          0,
          max(merged, d => d.y)
        ])
        .range([
          bounds.y + bounds.height,
          bounds.y
        ])
        .nice(counts.y)

      return {x, y}
    },

    ticks () {
      const {scale, counts} = this

      if (!scale) {
        return {
          x: [],
          y: []
        }
      }

      return {
        x: scale.x.ticks(counts.x),
        y: scale.y.ticks(counts.y)
      }
    }
  },
  methods: {
    resize () {
      const {
        width,
        height
      } = this.$refs.surface.getBoundingClientRect()

      this.bounds = {
        x: 0,
        y: 0,
        width,
        height
      }
    },

    enter (region) {
      region.active = true
    },

    leave (region) {
      region.active = false
    }
  },
  watch: {
    data () {
      this.regions = this.data.reduce((regions, data, i) => {
        data.forEach(point => {
          if (!regions[point.x]) {
            regions[point.x] = {
              label: point.x,
              id: this.$identifier(),
              plots: [],
              active: false
            }
          }

          const plot = this.plots[i]

          regions[point.x].plots.push({
            label: plot.label,
            priority: plot.priority,
            x: point.x,
            y: point.y
          })
        })

        return regions
      }, [])
    },

    axis () {
      // When axes are added or removed, schedule a resize of the chart for the
      // next tick. This gives the chart a chance to update its padding before
      // its bounds are re-computed, rather than the bounds being re-computed
      // before the padding is applied.
      this.$nextTick(this.resize)
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
  },
  destroyed () {
    window.removeEventListener('resize', this.resize)
  }
})

export const ChartIcon = Component('v-chart-icon', {
  template: ChartIconTemplate,
  props: {
    priority: {
      type: [
        String,
        Number
      ],
      default: 1,
      validator (value) {
        return value > 0 && value <= 5
      }
    },
    size: {
      type: [
        String,
        Number
      ],
      default: 14
    }
  },
  computed: {
    classes () {
      return {
        [`is-priority-${this.priority}`]: true
      }
    }
  }
})

export const PlotMixin = {
  mixins: [
    MemberOf(chartPlots, 'chart')
  ],
  props: {
    data: {
      description: `
        The data of the plot.
      `,
      type: Array,
      default () {
        return []
      },
      required: true
    },
    priority: {
      description: `
        The priority of the plot, determining its visual presentation. Must be between 1 and 5.
      `,
      type: [
        String,
        Number
      ],
      default: 1,
      validator (value) {
        return value > 0 && value <= 5
      }
    }
  },
  computed: {
    points () {
      return this.data.map((d, i) => ({
        x: i,
        y: d
      }))
    },

    scale () {
      const {chart} = this

      if (!chart || !chart.scale) {
        return {
          x: x => x,
          y: y => y
        }
      }

      return chart.scale
    },

    regions () {
      const {chart} = this

      if (!chart || !chart.regions) {
        return this.points.map(point => ({
          plots: [point],
          active: false
        }))
      }

      return chart.regions
    }
  }
}

export const ChartLine = Component('v-chart-line', {
  mixins: [
    PlotMixin
  ],
  template: ChartLineTemplate,
  props: {
    type: {
      description: `
        The type of the line plot, determining its curvature. See <a href="https://github.com/d3/d3-shape#curves" target="_blank">https://github.com/d3/d3-shape#curves</a> for examples.
      `,
      type: String,
      default: 'linear',
      valid: [
        'basis',
        'linear',
        'natural',
        'monotone-x',
        'monotone-y',
        'step',
        'step-before',
        'step-after'
      ]
    },
    markers: {
      description: `
        Whether or not to display markers on the line plot. Markers are disabled automatically if points are packed too tight within the plot.
      `,
      type: Boolean,
      default: true
    },
    label: {
      description: `
        The label of the line plot. This will be displayed in tooltips and the legend.
      `,
      type: String,
      default: null,
      required: true
    }
  },
  computed: {
    classes () {
      return {
        [`is-priority-${this.priority}`]: true
      }
    },

    curve () {
      const {type} = this

      switch (type) {
        case 'basis': return curveBasis
        case 'linear': return curveLinear
        case 'natural': return curveNatural
        case 'monotone-x': return curveMonotoneX
        case 'monotone-y': return curveMonotoneY
        case 'step': return curveStep
        case 'step-before': return curveStepBefore
        case 'step-after': return curveStepAfter
      }
    },

    line () {
      const {scale} = this

      const lineFn = line()
        .x((d, i) => scale.x(i))
        .y((d, i) => scale.y(d))
        .curve(this.curve)

      return lineFn(this.data)
    }
  }
})

export const ChartAxis = Component('v-chart-axis', {
  mixins: [
    MemberOf(chartAxis, 'chart')
  ],
  template: ChartAxisTemplate,
  props: {
    side: {
      description: `
        The side of the chart on which to render the axis.
      `,
      type: String,
      default: null,
      required: true,
      valid: [
        'top',
        'right',
        'bottom',
        'left'
      ]
    }
  },
  computed: {
    classes () {
      return {
        [`is-${this.side}`]: this.side
      }
    },

    axis () {
      const {side} = this
      return side === 'top' || side === 'bottom'
        ? ['x', 'y']
        : ['y', 'x']
    },

    scale () {
      const {chart} = this

      if (chart) {
        return chart.scale
      }
    },

    domain () {
      const {scale} = this

      if (scale) {
        return scale[this.axis[0]].domain()
      }
    },

    origin () {
      const {domain, side} = this

      if (domain) {
        return domain[0] + (
          side === 'top' || side === 'right'
            ? domain[1]
            : 0
        )
      }
    },

    center () {
      const {domain} = this

      if (domain) {
        return (domain[0] + domain[1]) / 2
      }
    },

    offset () {
      const {scale, axis, side} = this
      const {ticks} = this.$refs

      if (!scale || !ticks) {
        return 0
      }

      const {width, height} = ticks.getBBox()

      const offset = axis[0] === 'x' ? height : width

      const sign = side === 'right' || side === 'bottom' ? -1 : 1

      return scale[axis[1]].invert(scale[axis[1]](0) - offset) * sign
    },

    label () {
      const {origin, center, offset, axis} = this

      return {
        [axis[0]]: center,
        [axis[1]]: origin + offset
      }
    },

    ticks () {
      const {chart, domain, origin, axis} = this

      if (!chart || !domain) {
        return []
      }

      return chart.ticks[axis[0]].map(tick => ({
        label: tick,
        [axis[0]]: tick,
        [axis[1]]: origin
      }))
    }
  }
})

export const ChartGrid = Component('v-chart-grid', {
  mixins: [
    MemberOf(chartGrid, 'chart')
  ],
  template: ChartGridTemplate,
  computed: {
    scale () {
      const {chart} = this

      if (chart) {
        return chart.scale
      }
    },

    bounds () {
      const {chart} = this

      if (chart) {
        return chart.bounds
      }
    },

    ticks () {
      const {chart} = this

      if (chart) {
        return chart.ticks
      }
    },

    lines () {
      const {ticks, bounds, scale} = this

      if (ticks) {
        return {
          x: ticks.x.map(tick => ({
            x1: scale.x(tick),
            x2: scale.x(tick),
            y1: bounds.y,
            y2: bounds.y + bounds.height
          })),
          y: ticks.y.map(tick => ({
            x1: bounds.x,
            x2: bounds.x + bounds.width,
            y1: scale.y(tick),
            y2: scale.y(tick)
          }))
        }
      }
    },

    markers () {
      const {chart, bounds, scale} = this

      if (scale) {
        const {regions} = chart
        const ticks = scale.x.ticks(regions.length)

        return ticks.map((tick, i) => ({
          active: regions[i].active,
          line: {
            x1: scale.x(tick),
            x2: scale.x(tick),
            y1: bounds.y,
            y2: bounds.y + bounds.height
          }
        }))
      }
    }
  }
})
